/**
 * Jain Gaurav Yatra — Guides (Guide Master) CRUD API.
 * Backend: /jain_gaurav_yatra/guides/  (cookie-session auth + X-Tenant-ID).
 * JSON body (no file uploads), paginated list response.
 *
 * Note: anonymous callers get a trimmed row (id, full_name, experience_years, languages) — the
 * contact details, bios, internal notes and status are only returned to authenticated admins.
 * `toGuide` is tolerant of both shapes.
 */
import { extractAdminTokens, getStoredAdminSession } from "@/lib/admin-login-api";

function apiBase(): string {
  return (import.meta.env.VITE_API_BASE_URL as string | undefined)?.replace(/\/$/, "") ?? "";
}

function tenantId(): string {
  return (import.meta.env.VITE_X_TENANTID as string | undefined) ?? "";
}

function authHeaders(): HeadersInit {
  const id = tenantId();
  const { access } = extractAdminTokens(getStoredAdminSession()?.session);
  return {
    "Content-Type": "application/json",
    ...(id ? { "X-Tenant-ID": id } : {}),
    ...(access ? { Authorization: `Bearer ${access}` } : {}),
  };
}

function errMessage(data: unknown, status: number): string {
  const d = data as Record<string, unknown>;
  const pick = (v: unknown) => (typeof v === "string" ? v : null);
  const msg =
    pick(d?.message) ??
    pick(d?.detail) ??
    (Array.isArray(d?.non_field_errors) ? pick((d.non_field_errors as string[])[0]) : null);
  if (msg) {return msg;}
  try {
    return JSON.stringify(data);
  } catch {
    return `Request failed (${status})`;
  }
}

const GUIDES_PATH = "/jain_gaurav_yatra/guides/";

export type GuideStatus = "pending" | "approved" | "rejected";

export const GUIDE_STATUSES: { value: GuideStatus; label: string }[] = [
  { value: "pending", label: "Pending" },
  { value: "approved", label: "Approved" },
  { value: "rejected", label: "Rejected" },
];

export const GUIDE_GENDERS: { value: string; label: string }[] = [
  { value: "male", label: "Male" },
  { value: "female", label: "Female" },
  { value: "other", label: "Other" },
];

/** Page-facing guide shape. */
export interface Guide {
  id: string;
  full_name: string;
  phone: string;
  email: string;
  area: string;
  gender: string;
  experience_years: number;
  charges_per_day: number;
  languages: string[];
  bio_en: string;
  bio_hi: string;
  bio_gu: string;
  internal_notes: string;
  status: GuideStatus;
  is_active: boolean;
  created_at: string;
}

/** Fields accepted when creating/updating. */
export interface GuideWrite {
  full_name: string;
  phone: string;
  email: string;
  area: string;
  gender: string;
  experience_years: number;
  charges_per_day: number;
  languages: string[];
  bio_en: string;
  bio_hi: string;
  bio_gu: string;
  internal_notes: string;
  status: GuideStatus;
  is_active: boolean;
}

function str(v: unknown): string {
  return v === null || v === undefined ? "" : String(v);
}

function num(v: unknown): number {
  if (v === null || v === undefined || v === "") {return 0;}
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
}

/** languages reads as an array; tolerate a comma string too. */
function toLanguages(v: unknown): string[] {
  if (Array.isArray(v)) {
    return v.map(str).filter((s) => s.trim());
  }
  const s = str(v);
  return s ? s.split(",").map((x) => x.trim()).filter(Boolean) : [];
}

function toStatus(v: unknown): GuideStatus {
  const s = str(v).toLowerCase();
  return s === "approved" || s === "rejected" ? s : "pending";
}

function toGuide(row: Record<string, unknown>): Guide {
  return {
    id: str(row.id),
    full_name: str(row.full_name),
    phone: str(row.phone),
    email: str(row.email),
    area: str(row.area),
    gender: str(row.gender),
    experience_years: num(row.experience_years),
    charges_per_day: num(row.charges_per_day_inr ?? row.charges_per_day),
    languages: toLanguages(row.languages),
    bio_en: str(row.bio_en),
    bio_hi: str(row.bio_hi),
    bio_gu: str(row.bio_gu),
    internal_notes: str(row.internal_notes),
    status: toStatus(row.status),
    is_active: row.is_active !== false,
    created_at: str(row.created_at),
  };
}

function toBody(p: GuideWrite): string {
  return JSON.stringify({
    full_name: p.full_name,
    phone: p.phone,
    email: p.email,
    area: p.area,
    gender: p.gender,
    experience_years: p.experience_years,
    charges_per_day_inr: p.charges_per_day,
    languages: p.languages,
    bio_en: p.bio_en,
    bio_hi: p.bio_hi,
    bio_gu: p.bio_gu,
    internal_notes: p.internal_notes,
    status: p.status,
    is_active: p.is_active,
  });
}

async function parseJson(res: Response): Promise<unknown> {
  return res.json().catch(() => ({}));
}

function rowsOf(data: unknown): Record<string, unknown>[] {
  const rows = Array.isArray(data)
    ? data
    : Array.isArray((data as { results?: unknown[] })?.results)
      ? (data as { results: unknown[] }).results
      : [];
  return rows as Record<string, unknown>[];
}

export async function listGuides(): Promise<Guide[]> {
  const base = apiBase();
  if (!base) {throw new Error("API base URL not configured (VITE_API_BASE_URL).");}
  const res = await fetch(`${base}${GUIDES_PATH}`, {
    method: "GET",
    headers: authHeaders(),
    credentials: "include",
  });
  const data = await parseJson(res);
  if (!res.ok) {throw new Error(errMessage(data, res.status));}
  return rowsOf(data).map(toGuide);
}

export async function createGuide(payload: GuideWrite): Promise<Guide> {
  const base = apiBase();
  const res = await fetch(`${base}${GUIDES_PATH}`, {
    method: "POST",
    headers: authHeaders(),
    credentials: "include",
    body: toBody(payload),
  });
  const data = await parseJson(res);
  if (!res.ok) {throw new Error(errMessage(data, res.status));}
  return toGuide(data as Record<string, unknown>);
}

export async function updateGuide(id: string, payload: GuideWrite): Promise<Guide> {
  const base = apiBase();
  const res = await fetch(`${base}${GUIDES_PATH}${id}/`, {
    method: "PATCH",
    headers: authHeaders(),
    credentials: "include",
    body: toBody(payload),
  });
  const data = await parseJson(res);
  if (!res.ok) {throw new Error(errMessage(data, res.status));}
  return toGuide(data as Record<string, unknown>);
}

export async function deleteGuide(id: string): Promise<void> {
  const base = apiBase();
  const res = await fetch(`${base}${GUIDES_PATH}${id}/`, {
    method: "DELETE",
    headers: authHeaders(),
    credentials: "include",
  });
  if (!res.ok && res.status !== 204) {
    const data = await parseJson(res);
    throw new Error(errMessage(data, res.status));
  }
}
